import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from } from '@helpers/media';
import ChevronDown from '@img/icons/header/chevron-down.svg?url';
import styled from 'styled-components';

const Container = styled.div`
  display: grid;

  // Products variant is 'A' - display 3 columns
  [data-narrow-width='true'] & {
    grid-template-columns: repeat(3, 1fr);
  }

  // Products variant is 'B' - display 2 columns
  [data-narrow-width='false'] & {
    grid-template-columns: 1fr 3fr;
  }
`;

const Column = styled.ul`
  display: flex;
  flex-direction: column;

  // Product dropdown variant is 'A'
  [data-narrow-width='true'] & {
    padding: 0 24px;
    border-right: 2px solid ${brand.grey[95]};
    overflow-y: auto;
    max-height: ${fluid(462, 594, Device.Desktop, Device.ActualDesktop)};

    &::-webkit-scrollbar {
      width: 20px;
    }

    &::-webkit-scrollbar-track {
      background: ${brand.white};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${brand.grey[80]};
      border-radius: 6px;
      border: 8px solid ${brand.white};
      border-top-width: 4px;
      border-bottom-width: 4px;
    }

    &:first-of-type {
      padding-left: 0;
    }

    &[data-scrollbar='true'] {
      position: relative;
      padding-right: 16px;

      // Scroll arrow
      ::before {
        content: '';
        display: block;
        position: absolute;
        z-index: 1;
        bottom: 0;
        right: 0;
        width: 12px;
        height: 30px;
        background: url(${ChevronDown}) no-repeat center center / 12px 6px;
        pointer-events: none;
      }

      // Scroll gradient mask at the bottom
      &::after {
        content: '';
        display: block;
        position: sticky;
        z-index: 1;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 32px;
        mask-image: linear-gradient(to bottom, rgb(255 255 255 / 1%) 0%, ${brand.white} 80%);
        background-color: ${brand.white};
        pointer-events: none;
      }
    }

    &[data-scroll-arrow='true']::before {
      display: none;
    }

    &[data-active='false'] {
      display: none;
    }

    &[data-active='true'] {
      display: block;

      &:last-of-type,
      &[data-last-dropdown='true'] {
        border-right: none;
      }
    }
  }

  // Product dropdown variant is 'B'
  [data-narrow-width='false'] & {
    --columns: 4;

    @media ${from(Device.DesktopLarge)} {
      --columns: 5;
    }

    padding: 0 ${fluid(0, 24, Device.DesktopLarge, Device.ActualDesktop)};

    &:first-of-type {
      gap: 8px;
      padding-left: 0;
      min-width: 25vw;
    }

    &:nth-child(2) {
      display: block;
      width: 100%;
      columns: auto var(--columns);
      padding: 0;

      > li {
        display: contents;

        > ul {
          &[data-visible='false'] {
            display: none;
          }

          &[data-visible='true'] {
            display: block;
            width: auto;
            min-width: calc(20% - 10px);
            margin-bottom: 10px;
            break-inside: avoid;

            // Sets min height for the column to force columns to wrap into 4 columns
            &[data-force-four-columns='true'] {
              min-height: 400px;
            }

            @media ${from(Device.DesktopLarge)} {
              // Sets min height for the column to force columns to wrap into 5 columns
              &[data-force-five-columns='true'] {
                min-height: 400px;
              }
            }

            // Sets min width for the column to force columns to wrap into 2 columns
            &[data-split-columns='true'] {
              position: relative;
              columns: auto 2;
              min-width: 184%;
              break-after: column;
              padding-top: 30px;

              @media ${from(Device.ActualDesktop)} {
                min-width: 200%;
              }

              li:first-child {
                position: absolute;
                top: 0;
                left: 0;
              }
            }
          }

          li {
            padding: 5px;

            &:first-child {
              ${fonts.helvetica['73-bold-extended']}
              font-size: 14px;
              line-height: 1.3em;
              text-transform: uppercase;
            }

            a:hover {
              color: ${brand.red.base};
              text-decoration: underline;
              text-underline-offset: 2px;
            }
          }
        }
      }
    }
  }
`;

const ProductDropdownStyles = {
  Container,
  Column,
};

export default ProductDropdownStyles;
